import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const KHOLEY_PHOTO = "https://customer-assets.emergentagent.com/job_842fdc3c-a0c8-43ee-a566-98abe6f2060f/artifacts/35a4eimv_Facetune_23-09-2023-17-06-43.jpeg";
const BEEKEEPER_INSPECT = "https://static.prod-images.emergentagent.com/jobs/842fdc3c-a0c8-43ee-a566-98abe6f2060f/images/0c087d0c57032421985cc6b503d68b29c65dd2393039060b88845abbec17d5b8.png";
const BEEKEEPER_FRAME = "https://static.prod-images.emergentagent.com/jobs/842fdc3c-a0c8-43ee-a566-98abe6f2060f/images/bd0cd3fe6783af4e357053d62fdbf5975cba6772c2d1548a46e4e805a1c1563b.png";
const DESERT_HIVES = "https://static.prod-images.emergentagent.com/jobs/842fdc3c-a0c8-43ee-a566-98abe6f2060f/images/dd048269a49c0b74891c6e62aaf18bc3acecfda4304123dded9ce871293029f5.png";

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      <section className="py-20 text-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="t-label c-gold mb-4">Our Story</p>
          <h1 className="t-headline text-5xl md:text-6xl lg:text-7xl c-cream mb-4">Bee Boosted</h1>
          <div className="gold-line-long mx-auto" />
        </div>
      </section>

      <section className="py-20 bg-bb-rich">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="aspect-[4/5] max-w-lg mx-auto lg:mx-0 overflow-hidden border border-bb-border">
            <img src={KHOLEY_PHOTO} alt="Kholey — Founder of Bee Boosted" className="w-full h-full object-cover" data-testid="kholey-photo" />
          </div>
          <div>
            <p className="t-label c-gold mb-4">The Visionary</p>
            <h2 className="t-headline text-4xl md:text-5xl c-cream mb-2"><em>Kholey</em></h2>
            <p className="t-label c-muted mb-6">Entrepreneur &middot; Wellness Pioneer &middot; Published Author</p>
            <div className="gold-line mb-8" />
            <div className="space-y-5 t-body c-muted">
              <p>Kholey is a dynamic entrepreneur and creative visionary with over 22 years of expertise in brand development, digital strategy, and online visibility. As the founder behind multiple successful ventures spanning luxury, wellness, and service industries, she brings a rare blend of innovation, spiritual depth, and business acumen to everything she creates.</p>
              <p>Her diverse heritage — rooted in Cherokee, Irish, and Black and Brown American traditions — shines through in her multifaceted approach to life and wellness. Kholey is a metaphysical researcher who integrates natural remedies, wellness rituals, and energetic alignment into her creative projects, bringing intentionality to every product and every experience.</p>
              <p>Her deep passion for the healing power of nature led to the creation of Bee Boosted — a luxury brand dedicated to sourcing the purest, most potent bee products from California and Arizona. Every jar, every tincture, and every honeycomb represents her unwavering commitment to quality and holistic well-being.</p>
              <p>
                Kholey is also the founder of{' '}
                <a href="https://gemstoneminerals.com/" target="_blank" rel="noopener noreferrer" className="c-gold hover:underline underline-offset-4" data-testid="about-gemstone-link">Gemstone Minerals</a>
                , where she curates ethically sourced crystals and explores the ancient intelligence of Earth's gemstones. She is currently developing <em>The Pulse Theory of Civilizational Transformation</em> and its companion volume <em>Crystals of the Pulse</em>, blending historical cycles with spiritual insight.
              </p>
            </div>
            <blockquote className="mt-10 pl-6 border-l-2 border-bb-gold">
              <p className="font-heading text-xl c-cream italic leading-relaxed">"Energy precedes form. What you carry within you determines what you receive. I created Bee Boosted to share nature's most extraordinary gifts with those who seek genuine wellness."</p>
              <cite className="t-label c-gold block mt-3 not-italic">— Kholey</cite>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="py-20 bg-bb-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="t-label c-gold mb-4">Excellence</p>
            <h2 className="t-headline text-4xl c-cream">A Legacy of Vision</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 text-center">
            {[
              { stat: "22+", label: "Years of Expertise", desc: "Digital strategy, brand development & online visibility" },
              { stat: "5", label: "Ventures Founded", desc: "Luxury, wellness, and service industries" },
              { stat: "2", label: "Published Works", desc: "Crystals of the Pulse & The Pulse Theory" },
              { stat: "2", label: "Pristine States", desc: "California & Arizona sourcing" },
            ].map((item, i) => (
              <div key={i} data-testid={`credential-${i}`}>
                <span className="font-heading text-5xl c-gold block mb-2">{item.stat}</span>
                <h3 className="t-label c-cream mb-2">{item.label}</h3>
                <p className="t-body c-muted text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-bb-rich">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="t-label c-gold mb-4">Our Principles</p>
            <h2 className="t-headline text-4xl c-cream">The Bee Boosted Way</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10 text-center">
            {[
              { title: "Provenance", desc: "We source exclusively from family-owned apiaries in California and Arizona that share our standards of excellence and sustainable beekeeping practices." },
              { title: "Purity", desc: "No additives, no shortcuts, no compromise. Our products are as nature intended — pure, potent, and deeply transformative for body and spirit." },
              { title: "Intention", desc: "Every product carries intentionality — from harvest to jar. We honor the sacred work of bees and the ancient traditions of natural wellness." },
            ].map((v, i) => (
              <div key={i}>
                <span className="t-label c-gold block mb-3">0{i + 1}</span>
                <h3 className="font-heading text-2xl c-cream mb-3">{v.title}</h3>
                <div className="gold-line mx-auto mb-4" />
                <p className="t-body c-muted text-sm">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BEEKEEPING PARTNERS ===== */}
      <section className="py-20 bg-bb-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="t-label c-gold mb-4">Our Partners</p>
            <h2 className="t-headline text-4xl c-cream mb-4">Family-Owned Apiaries</h2>
            <p className="t-body c-muted max-w-2xl mx-auto text-sm">
              Every Bee Boosted product begins with our carefully selected beekeeping partners across California and Arizona — dedicated artisans who share our unwavering commitment to quality and sustainable practices.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="overflow-hidden border border-bb-border group hover:border-bb-gold transition-colors" data-testid="beekeeper-img-1">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={BEEKEEPER_INSPECT} alt="Beekeeper carefully inspecting honeycomb frames in a California apiary" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg c-cream mb-2">Meticulous Inspection</h3>
                <p className="t-body text-sm c-muted">Every frame is hand-inspected to ensure the highest standards of hive health and honey purity before harvest.</p>
              </div>
            </div>
            <div className="overflow-hidden border border-bb-border group hover:border-bb-gold transition-colors" data-testid="beekeeper-img-2">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={BEEKEEPER_FRAME} alt="Beekeeper holding a golden honeycomb frame at sunset" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg c-cream mb-2">Artisan Harvesting</h3>
                <p className="t-body text-sm c-muted">Our partners use gentle, traditional harvesting methods that protect the colony while preserving every bioactive compound.</p>
              </div>
            </div>
            <div className="overflow-hidden border border-bb-border group hover:border-bb-gold transition-colors" data-testid="beekeeper-img-3">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={DESERT_HIVES} alt="Beehives in the Arizona Sonoran Desert landscape at golden hour" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg c-cream mb-2">Pristine Desert Apiaries</h3>
                <p className="t-body text-sm c-muted">Our Arizona hives thrive amid the ancient Sonoran Desert, where bees forage from mesquite, saguaro, and desert wildflowers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-bb-rich border-t border-bb-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="t-headline text-4xl c-cream mb-6">Experience the Difference</h2>
          <p className="t-body c-muted mb-10">Discover our collection of rare bee elixirs, each one a testament to nature's perfection and Kholey's vision for holistic wellness.</p>
          <Link to="/products"><button className="btn-primary" data-testid="about-shop-button">Shop the Collection <ArrowRight className="w-4 h-4" /></button></Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
